// Third party components
import React from 'react';
import PropTypes from 'prop-types';

// MUI components
import { Container, Divider, Grid, Skeleton, Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';

// Own components
import ActivitiesList from './ActivitiesList';

/**
 * Left side nav list with a right side details explorer.
 * @param {Object} props - The props for the component.
 * @param {Array<Object>} props.items - Array of arbitrary data objects.
 * @param {bool} props.loading - Loading state.
 * @param {string} props.singularName - Singular variant of the name for the main entity - will be used in headers/info.
 * @param {string} props.pluralName - Plural variant of the name for the main entity - will be used in headers/info.
 * @param {string} props.nameField - Name of the accessor field on items entity that contains the name of the item.
 * @param {string} props.parentDetailsSingularName - Singular variant of the name for the parent's main entity - will be used in first details info section on the top right.
 * At the time of this writing, it always is practice related information, while the details list is always activity related info.
 * @param {string} props.parentDetailsNameField - Name of the accessor field on items entity that contains the name of the item.
 * @param {string} props.parentDetailsGlobalIdNameField - Name of the accessor field on items entity that contains the globalid of the item.
 * @param {Array<Object>} props.items - Array of details config objects for ordering, labeling, and accessing data on props.items.
 * @component
 * @example
 * const [loading, setLoading] = useState(false);
 * return (
 *   <DetailsExplorer
 *       items={[{activityName: 'blah'}, {activityName: 'blah'}]}
 *       loading={loading}
 *       singularName="Activity"
 *       pluralName="Activities"
 *       nameField="activityName"
 *       parentDetailSingularName="Practice"
 *       parentDetailNameField="practiceName"
 *       parentDetailsGlobalIdNameField="practiceGlobalId"
 *       detailsConfig={[{ order: 0, label: 'Activity Name', field: 'activityName' }]}
 *     />
 * )
 * @see https://mui.com/material-ui/react-list/
 * @see https://mui.com/material-ui/react-grid/
 */
const DetailsExplorer = ({ items, loading, singularName, pluralName, nameField,
  parentDetailsSingularName, parentDetailsNameField, parentDetailsGlobalIdNameField, detailsConfig }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  let selectedItem = items ? items[selectedIndex] : null;

  const handleListItemClick = (index) => {
    setSelectedIndex(index);
    if (items) {
      selectedItem = items[index];
    }
  }

  const getParentLinkHref = (singularName, parentDetailsGlobalIdNameField, selectedItem) => {
    // For now the parent is always a practice

    let linkHref = ''
    if (singularName?.toLowerCase() === 'activity') {
      if (selectedItem) {
        let pathSegment = selectedItem[parentDetailsGlobalIdNameField];
        linkHref = `/practices/${pathSegment.replace(/[{}]/g, '')}`
      }
    } else if (singularName?.toLowerCase() === 'practice') {
      linkHref = `/practices/${selectedItem?.globalid.replace(/[{}]/g, '')}`
    }
    return linkHref;
  }

  // Render function for the right side list
  const renderDetailsPanelOnRight = () => {
    if (!items || !selectedItem || loading) {
      return <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    }

    return (
      <>
        {/* Top details container - only show it if there are parent details to show */}
        {(parentDetailsSingularName && parentDetailsNameField) ?
          <Grid container spacing={2} sx={{ paddingLeft: 5, paddingTop: 3, paddingBottom: 3 }}>

            {/* Parent (practice) details if available */}
            <>
              <Grid item xs={6} sx={{ paddingTop: 3, paddingLeft: 3, fontSize: 18 }}>
                <b>{parentDetailsSingularName} Name: </b>
              </Grid>
              <Grid item xs={6} textAlign="right" sx={{fontSize: 18}}>
                {
                  selectedItem?.[parentDetailsNameField] || 'Not available'
                }
              </Grid>
            </>

            {/* Link to the details page of the selected item.
              * Show only for Activities that have a parent Practice */}
            {(selectedItem?.[parentDetailsNameField]) ?
              <Grid item xs={12} textAlign="right">
                <Link
                  href={getParentLinkHref(singularName, parentDetailsGlobalIdNameField, selectedItem)}
                ><strong>Go to {parentDetailsSingularName}</strong></Link>
              </Grid>
              :
              <></>
            }
          </Grid>
          :
          <></>
        }

        <Divider />

        {/* Details List container */}
        <ActivitiesList
          activities={selectedItem.activities || [selectedItem] } // Can be a list of one
          detailsConfig={detailsConfig}
          singularName={singularName} />
      </>
    );
  }

  // Main component return
  return (
    <>
      {/* Container for the main content */}
      <Grid container spacing={0}>

        {/* Container for left most side panel */}
        <Grid item xs={2.5}>
          {/* TODO: Could move this to a left render function */}
          <Container maxWidth={false} sx={{ height: '100%' }} disableGutters>

            {/* Header */}
            <Typography variant="panelHeader">{!items ? (<Skeleton height={50} />) : `${pluralName} List`}</Typography>

            <Divider />

            {/* Iterative list of items */}
            <List component="nav" aria-label={`${pluralName} list`}>

              {items?.map((item, itemIndex) => {
                return (
                  // Main button body with fancy hover effect
                  <ListItemButton
                    key={itemIndex}
                    selected={selectedIndex === itemIndex}
                    onClick={() => handleListItemClick(itemIndex)}
                  >
                    {/* Item name */}
                    <ListItemText primary={item?.[nameField] || 'Not available'} />
                  </ListItemButton>
                );
              })}

            </List>

          </Container>
        </Grid>

        {/* Container for right side details */}
        <Grid item xs={9.5} sx={{ paddingLeft: 3, paddingRight: 3 }}>

          {/* Header */}
          <Typography variant="sectionHeader">Details</Typography>

          <Divider />

          {/* Render function that contains the structure for the right most details pane */}
          {renderDetailsPanelOnRight()}
        </Grid>

      </Grid>
    </>
  );
}

DetailsExplorer.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  singularName: PropTypes.string,
  pluralName: PropTypes.string,
  nameField: PropTypes.string,
  parentDetailsSingularName: PropTypes.string,
  parentDetailsNameField: PropTypes.string,
  parentDetailsGlobalIdNameField: PropTypes.string,
  detailsConfig: PropTypes.arrayOf(PropTypes.shape({
    order: PropTypes.number,
    label: PropTypes.string,
    field: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    isDate: PropTypes.bool,
  })),
}

export default DetailsExplorer;
